/**
 * Central endpoint map. All URLs are relative to VITE_API_BASE_URL, which
 * MUST already end in /api. Do NOT prefix strings here with /api.
 */
export const endpoints = {
  auth: {
    login: "/auth/login",
    logout: "/auth/logout",
    me: "/auth/me",
    forgotPassword: "/auth/forgot-password",
    resetPassword: "/auth/reset-password",
    verifyEmail: "/auth/verify-email",
    resendVerification: "/auth/resend-verification",
    sessions: "/auth/sessions",
    session: (publicId: string) => `/auth/sessions/${publicId}`,
    csrf: "/auth/csrf",
  },
  public: {
    settings: "/public/settings",
    navigation: "/public/navigation",
    home: "/public/home",
    teams: "/public/teams",
    team: (id: string) => `/public/teams/${id}`,
    players: "/public/players",
    player: (id: string) => `/public/players/${id}`,
    staff: "/public/staff",
    fixtures: "/public/fixtures",
    fixture: (id: string) => `/public/fixtures/${id}`,
    results: "/public/results",
    match: (id: string) => `/public/matches/${id}`,
    matchLiveState: (id: string) => `/public/matches/${id}/live-state`,
    liveMatch: "/public/live-match",
    leagueTables: "/public/league-tables",
    competitionHistory: "/public/competition-history",
    competitionHistorySeasons: (slug: string) => `/public/competitions/${slug}/history-seasons`,
    competitionHistoryStandings: (slug: string, season: string) =>
      `/public/competitions/${slug}/${encodeURIComponent(season.replace("/", "--"))}/standings`,
    competitionHistoryMatches: (slug: string, season: string) =>
      `/public/competitions/${slug}/${encodeURIComponent(season.replace("/", "--"))}/matches`,
    competitionHistoryBracket: (slug: string, season: string) =>
      `/public/competitions/${slug}/${encodeURIComponent(season.replace("/", "--"))}/bracket`,
    historicalMatch: (id: string) => `/public/matches/${id}/history`,
    streams: "/public/streams",
    stream: (id: string) => `/public/streams/${id}`,
    articles: "/public/articles",
    article: (slug: string) => `/public/articles/${slug}`,
    videos: "/public/videos",
    galleries: "/public/galleries",
    gallery: (id: string) => `/public/galleries/${id}`,
    sponsors: "/public/sponsors",
    captchaChallenge: "/public/captcha/challenge",
    formSubmit: (formId: string) => `/public/forms/${formId}/submissions`,
    newsletterSubscribe: "/public/newsletter/subscribe",
  },
  player: {
    dashboard: "/player/dashboard",
    profile: "/player/profile",
    contactDetails: "/player/contact-details",
    profileChangeRequests: "/player/profile-change-requests",
    fixtures: "/player/fixtures",
    results: "/player/results",
    statistics: "/player/statistics",
    trainingSessions: "/player/training-sessions",
    attendance: "/player/attendance",
    documents: "/player/documents",
    videos: "/player/videos",
    announcements: "/player/announcements",
    notifications: "/player/notifications",
    notificationPreferences: "/player/notification-preferences",
  },
  coaching: {
    dashboard: "/coaching/dashboard",
    team: (id: string) => `/coaching/teams/${id}`,
    teamPlayers: (id: string) => `/coaching/teams/${id}/players`,
    trainingSessions: "/coaching/training-sessions",
    trainingSession: (id: string) => `/coaching/training-sessions/${id}`,
    trainingAttendance: (id: string) => `/coaching/training-sessions/${id}/attendance`,
    matchSelections: "/coaching/match-selections",
    playerAssessments: "/coaching/player-assessments",
    reports: "/coaching/reports",
  },
  admin: {
    dashboard: "/admin/dashboard",
    users: "/admin/users",
    user: (id: string) => `/admin/users/${id}`,
    roles: "/admin/roles",
    role: (id: string) => `/admin/roles/${id}`,
    permissions: "/admin/permissions",
    teams: "/admin/teams",
    team: (id: string) => `/admin/teams/${id}`,
    teamArchive: (id: string) => `/admin/teams/${id}/archive`,
    players: "/admin/players",
    player: (id: string) => `/admin/players/${id}`,
    staff: "/admin/staff",
    staffMember: (id: string) => `/admin/staff/${id}`,
    fixtures: "/admin/fixtures",
    fixture: (id: string) => `/admin/fixtures/${id}`,
    competitions: "/admin/competitions",
    seasons: "/admin/seasons",
    venues: "/admin/venues",
    match: (id: string) => `/admin/matches/${id}`,
    matchEvents: (id: string) => `/admin/matches/${id}/events`,
    matchScore: (id: string) => `/admin/matches/${id}/score`,
    matchClock: (id: string) => `/admin/matches/${id}/clock`,
    matchStatus: (id: string) => `/admin/matches/${id}/status`,
    matchCommentary: (id: string) => `/admin/matches/${id}/commentary`,
    matchStatistics: (id: string) => `/admin/matches/${id}/statistics`,
    matchLineups: (id: string) => `/admin/matches/${id}/lineups`,
    streams: "/admin/streams",
    stream: (id: string) => `/admin/streams/${id}`,
    streamTest: (id: string) => `/admin/streams/${id}/test`,
    streamStatus: (id: string) => `/admin/streams/${id}/status`,
    streamIncidents: (id: string) => `/admin/streams/${id}/incidents`,
    articles: "/admin/articles",
    article: (id: string) => `/admin/articles/${id}`,
    articlePublish: (id: string) => `/admin/articles/${id}/publish`,
    articleSchedule: (id: string) => `/admin/articles/${id}/schedule`,
    media: "/admin/media",
    mediaUpload: "/admin/media/upload",
    mediaItem: (id: string) => `/admin/media/${id}`,
    galleries: "/admin/galleries",
    gallery: (id: string) => `/admin/galleries/${id}`,
    sponsors: "/admin/sponsors",
    sponsor: (id: string) => `/admin/sponsors/${id}`,
    forms: "/admin/forms",
    form: (id: string) => `/admin/forms/${id}`,
    enquiries: "/admin/enquiries",
    enquiry: (id: string) => `/admin/enquiries/${id}`,
    newsletterSubscribers: "/admin/newsletter-subscribers",
    newsletterSubscriber: (id: string) => `/admin/newsletter-subscribers/${id}`,
    reports: "/admin/reports",
    analytics: "/admin/analytics",
    auditLogs: "/admin/audit-logs",
    settings: "/admin/settings",
    homepageBlocks: "/admin/homepage-blocks",
    homepageBlock: (id: string) => `/admin/homepage-blocks/${id}`,
    homepageReorder: "/admin/homepage-blocks/reorder",
    profileChangeRequests: "/admin/profile-change-requests",
    profileChangeRequest: (id: string) => `/admin/profile-change-requests/${id}`,
  },
} as const;
